import { useEffect, useMemo, useState } from 'react'

import { Button } from 'src/shared/components/ui/buttons/button'
import DashboardModal from 'src/shared/components/ui/dashboard/dashboard-modal'

import BusinessInfoInputField from '../components/business-info-input-field'
import type { BusinessInfo } from '../schemas/business-create'
import { BusinessInfoLabels } from '../schemas/business-create'
import { useBusinessCreateStore } from '../store/business-create-store'

type AddMissingInfoModalProps = {
  open: boolean
  onClose: () => void
  fields: (keyof BusinessInfo)[]
  onAllCompleted: () => void
}

const AddMissingInfoModal = ({ open, onClose, fields, onAllCompleted }: AddMissingInfoModalProps) => {
  const { formData, setFormData } = useBusinessCreateStore()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [draftValue, setDraftValue] = useState<any>(null)

  const queuedFields = useMemo(() => {
    return fields.filter((field, index) => fields.indexOf(field) === index)
  }, [fields])

  const currentField = queuedFields[currentIndex] ?? null
  const currentFieldValue = currentField ? formData[currentField] ?? null : null

  useEffect(() => {
    if (!open) return
    setCurrentIndex(0)
    setIsComplete(false)
    setDraftValue(null)
  }, [open, fields])

  useEffect(() => {
    if (!open) return
    setDraftValue(currentFieldValue)
  }, [currentField, currentFieldValue, open])

  const handleSaveCurrentField = () => {
    if (!currentField) return

    setFormData({
      ...formData,
      [currentField]: draftValue,
    })

    const isLast = currentIndex >= queuedFields.length - 1
    if (isLast) {
      handleContinue()
      return
    }

    setCurrentIndex((prev) => prev + 1)
  }

  const handleContinue = () => {
    onAllCompleted()
    onClose()
  }

  const isLastField = currentIndex >= queuedFields.length - 1
  const primaryButtonText = queuedFields.length === 0 || isComplete ? 'Continue' : isLastField ? 'Save' : 'Save & next'

  const handlePrimaryAction = () => {
    if (queuedFields.length === 0 || isComplete) {
      handleContinue()
      return
    }
    handleSaveCurrentField()
  }

  return (
    <DashboardModal open={open} onClose={onClose}>
      <div className="relative z-[100] flex w-[400px] flex-col gap-6 rounded-[25px] bg-white p-8">
        <div className="flex flex-col gap-2 text-center">
          <h2 className="text-[26px] font-semibold leading-tight text-primary">Add required information</h2>
          <p className="text-base leading-[1.6] text-muted">
            Fill in the missing details so Lynk can continue this workflow.
          </p>
        </div>

        {queuedFields.length === 0 ? (
          <div className="rounded-[20px] bg-accent px-4 py-6 text-center text-sm text-muted">
            No required fields to complete.
          </div>
        ) : (
          <div className="mb-2">
            <div className="mb-2 text-center text-xs font-medium text-muted">
              {Math.min(currentIndex + 1, queuedFields.length)} of {queuedFields.length}
            </div>
            <p className="text-center text-lg font-semibold text-primary">
              {BusinessInfoLabels[currentField as keyof BusinessInfo] ?? currentField}
            </p>
            <div className="mt-5 flex justify-center">
              <div className="w-full max-w-[560px]">
                {currentField ? (
                  <BusinessInfoInputField infoField={currentField} value={draftValue} onChange={setDraftValue} />
                ) : null}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="default" size="xl" className="rounded-full text-base" onClick={handlePrimaryAction}>
            {primaryButtonText}
          </Button>
        </div>
      </div>
    </DashboardModal>
  )
}

export default AddMissingInfoModal
